import { SwatchGroup } from '@components/alfakher/pylot-swatches/src/SwatchGroup/SwatchGroup'
import type { ConfigurableVariant } from '@pylot-data/pylotschema'
import cn from 'classnames'
import React, { ReactElement } from 'react'
import type { UseOptionSelectorReturn } from '@corratech/pylot-variant-selector/src/hooks/useOptionSelector'
import { getDisabledOptionValues } from '@corratech/pylot-variant-selector/src/utils/getDisabledOptionValues'
import s from '@corratech/pylot-variant-selector/src/VariantSelector/VariantSelector.module.scss'
import { VariantCount } from '@corratech/pylot-variant-selector/src/VariantCount'
import formStyles from '@commonStyles/Forms.module.scss'

export type VariantSelectorProps = UseOptionSelectorReturn & {
    disableAll: boolean
    variants: ConfigurableVariant[]
    showVariantCount?: boolean
    displaySwatchOptionLabel?: boolean
    viewMore?: boolean
    isCentered?: boolean
}

/**
 * Tells which attributes should be displayed as circular.
 * Hardcoded array to match previous designs.
 */
const circularAttributeCodes = ['size']

/**
 * Component that displays attributes and swatches.
 * Should be used in combination with `useOptionSelector` hook.
 * @param param0 product option API, that `useOptionSelector` hook provides.
 * @param param0.disableAll boolean value that indicates if parent product is *OUT_OF_STOCK*
 * @param param0.variants configurable product variants. Disabled options are calculated based on variant attributes.
 * @returns Varian selector representation (attributes and swatches)
 */
const VariantSelector = ({
    options,
    selectedOptions,
    setSelectedOptions,
    disableAll = false,
    showVariantCount = false,
    displaySwatchOptionLabel = false,
    variants = [],
    viewMore = true,
    isCentered = false
}: VariantSelectorProps): ReactElement => {
    const disabledValues = getDisabledOptionValues(
        options,
        selectedOptions,
        variants
    )

    return (
        <div>
            {options.map((opt, idx: number) => {
                const disabledValuesForOption = disabledValues?.[opt.code] ?? []
                return (
                    <div
                        className={cn({
                            ['pb-4']: idx !== options.length - 1
                        })}
                        key={opt.code}
                    >
                        {showVariantCount && (
                            <VariantCount
                                values={opt.values}
                                displayName={opt.displayName}
                            />
                        )}
                        {opt.displayName && (
                            <div
                                className={cn(
                                    'pdp-swatch-label',
                                    s['display-name']
                                )}
                            >
                                <span>{opt.displayName}</span>
                                <span className="font-bold">
                                    {selectedOptions[opt.code]}
                                </span>
                            </div>
                        )}
                        <section className={formStyles.root}>
                            <SwatchGroup
                                option={opt}
                                disableAll={disableAll}
                                disabledValues={disabledValuesForOption}
                                isCircle={circularAttributeCodes.includes(
                                    opt.code
                                )}
                                selectedOptions={selectedOptions}
                                setSelectedOptions={setSelectedOptions}
                                displaySwatchOptionLabel={
                                    displaySwatchOptionLabel
                                }
                                viewMore={viewMore}
                                // @ts-expect-error
                                isCentered={isCentered}
                            />
                        </section>
                    </div>
                )
            })}
        </div>
    )
}

export default VariantSelector
